/*
 * File: index.tsx
 * Project: meki
 * File Created: Monday, 3rd May 2021 3:14:26 pm
 * Author: Esperanza Horn (esperanza@inventures.cl)
 * -----
 * Last Modified: Monday, 27th November 2023 5:22:34 pm
 * Modified By: Blanca Munizaga (blanca@inventures.cl)
 * -----
 * Copyright 2020 - 2021 Incrementa Ventures SpA. ALL RIGHTS RESERVED
 * Terms and conditions defined in license.txt
 * -----
 * Inventures - www.inventures.cl
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Typography, GlobalStyles } from '@mui/material';
import { styled } from '@mui/system';
import { useQuery } from '@apollo/client';
import {
  getCategoryProducts,
  getCategoryProductsQueryParams,
  getCategoryProductsQueryResponse,
} from '@queries/category';
import { getRoute } from '@utils';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { UrlObject } from 'url';
import { removeUndefineds } from '@utils';
import { SearchContentContainer } from '@components/SearchContentContainer';
import { ProductFilterOptions } from '@components/ProductFilterOptions';
import { ProductsNotFound } from '@components/ProductsNotFound';
import dynamic from 'next/dynamic';
import { Products } from '@components/Product';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useBottomReach } from '@hooks/useBottomReach';
import { Header } from '@components/Header/Header';
const ProductDrawer = dynamic(() => {
  const promise = import('@components/ProductDrawer').then(
    (mod) => mod.ProductDrawer,
  );
  return promise;
});

const backgroundGlobalStyle = (
  <GlobalStyles
    styles={{
      body: {
        backgroundColor: '#f9f9f9 !important',
      },
    }}
  />
);

const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up(700)]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 8,
  },
  [theme.breakpoints.up(1230)]: {
    maxWidth: 1200,
    margin: 'auto',
  },
}));
const BackButton = styled(Typography)(() => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  margin: '16px 0 12px 4px',
  width: 'fit-content',
}));
interface CategoryIndexProps {
  initialData: getCategoryProductsQueryResponse;
}

export function CategoryIndex(props: CategoryIndexProps) {
  // Hooks init:
  const router = useRouter();
  const [toggleDrawer, setToggleDrawer] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [totalFiltersCheck, setTotalFiltersCheck] = useState(0);

  // Queries:
  const queryParams = useMemo(() => {
    const brands = router.query.brands
      ? (router.query.brands as string).split(',')
      : undefined;

    const priceRanges: { start?: number; stop?: number }[] = router.query
      .priceRanges
      ? [router.query.priceRanges].flat().map((priceRange) => {
          const queryObject = Object.fromEntries(
            priceRange.split(';').map((range) => range.split(':')),
          ) as { start?: number; stop?: number };

          return {
            start: +queryObject?.start ? +queryObject.start : undefined,
            stop: +queryObject?.stop ? +queryObject.stop : undefined,
          };
        })
      : undefined;

    return {
      brands,
      priceRanges,
    };
  }, [router.query]);
  const variables = useMemo(() => {
    return {
      params: {
        pageNumber: 1,
        slug: router.query.categorySlug as string,
        filterBy: {
          brands: queryParams.brands,
          priceRanges: queryParams.priceRanges,
        },
        sortBy: {
          price: router.query.sortBy == 'price' ? true : false,
          alphabetically:
            router.query.sortBy == 'alphabetically' ? true : false,
          mostSelling: router.query.sortBy == 'mostSelling' ? true : false,
        },
      },
      categoryParams: {
        slug: router.query.categorySlug as string,
      },
    };
  }, [
    queryParams.brands,
    queryParams.priceRanges,
    router.query.categorySlug,
    router.query.sortBy,
  ]);
  const {
    data: freshData,
    loading,
    fetchMore,
  } = useQuery<
    getCategoryProductsQueryResponse,
    getCategoryProductsQueryParams
  >(getCategoryProducts(), {
    variables,
    skip: !router.query.categorySlug,
    fetchPolicy: 'cache-and-network',
  });
  const data = freshData || props.initialData;
  const categoryData = data?.category;

  const handleFetchMore = useMemo(() => {
    if (
      data?.bestSellingProductsCategory?.pageInfo.totalPages <=
      (data?.bestSellingProductsCategory?.pageInfo.pageNumber ?? 1)
    ) {
      return;
    }
    return async () => {
      await fetchMore({
        variables: {
          ...variables,
          params: {
            ...variables.params,
            pageNumber:
              (data?.bestSellingProductsCategory?.pageInfo.pageNumber ?? 1) + 1,
          },
        },
      });
    };
  }, [
    data?.bestSellingProductsCategory?.pageInfo.totalPages,
    data?.bestSellingProductsCategory?.pageInfo.pageNumber,
    fetchMore,
    variables,
  ]);
  const { loading: loadingMore } = useBottomReach(handleFetchMore);

  const handleToggleDrawer = useCallback(
    (event?: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event &&
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      setToggleDrawer(!toggleDrawer);
    },
    [toggleDrawer],
  );

  const handleSort = useCallback(
    async (type: string) => {
      const newRoute = (): UrlObject => ({
        pathname: router.pathname,
        query: {
          ...router.query,
          sortBy: type,
        },
      });
      setNavigating(true);
      await router.push(newRoute());
      setNavigating(false);
    },
    [router],
  );

  const handleTotalFiltersChecked = useCallback((total: number) => {
    setTotalFiltersCheck(total);
  }, []);

  const handleCleanFilter = useCallback(async () => {
    const newRoute = (): UrlObject => ({
      pathname: router.pathname,
      query: removeUndefineds({
        ...router.query,
        brands: undefined,
        priceRanges: undefined,
        sortBy: router.query.sortBy,
      }),
    });

    setNavigating(true);
    await router.push(newRoute());
    setNavigating(false);
  }, [router]);

  return (
    <>
      {backgroundGlobalStyle}

      <Header hasSearchBox={true} />
      <SearchContentContainer>
        <HeaderContainer>
          {router.query.categorySlug && (
            <Link
              href={getRoute('subCategories', {
                categorySlug: router.query.categorySlug as string,
              })}
              passHref
            >
              <BackButton color="textPrimary">
                <KeyboardArrowLeftIcon />
                Atrás
              </BackButton>
            </Link>
          )}
          <ProductFilterOptions
            totalFiltersCheck={totalFiltersCheck}
            handleToggleDrawer={handleToggleDrawer}
            handleSort={handleSort}
            sortType={router.query.sortBy as string}
          />
        </HeaderContainer>

        <div>
          <div>
            {data?.bestSellingProductsCategory && (
              <ProductDrawer
                handleToggleDrawer={handleToggleDrawer}
                toggleDrawer={toggleDrawer}
                data={data?.bestSellingProductsCategory}
                handleTotalFiltersChecked={handleTotalFiltersChecked}
                setNavigating={setNavigating}
              />
            )}
            {data?.bestSellingProductsCategory?.nodes?.length > 0 ||
            loading ||
            navigating ? (
              <Products
                title={categoryData?.name}
                data={
                  navigating
                    ? []
                    : data?.bestSellingProductsCategory?.nodes?.map(
                        (product) => ({
                          product,
                        }),
                      ) ?? []
                }
                loading={loading || loadingMore || navigating}
              />
            ) : (
              <ProductsNotFound onClick={handleCleanFilter} />
            )}
          </div>
        </div>
      </SearchContentContainer>
    </>
  );
}
